import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, RotateCcw } from "lucide-react";
import { MESH_INFO } from "@/components/MeshPhoto";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
const abs = (u) => (u?.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

function Slot({ kind, photo, onChange }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("image", file);
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/mesh-photos/${kind}`, fd, auth());
      onChange(r.data);
      toast.success(`${MESH_INFO[kind].title} photo updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const reset = async () => {
    const r = await axios.delete(`${BACKEND_URL}/api/mesh-photos/${kind}`, auth());
    onChange(r.data);
    toast.success("Back to default photo");
  };
  return (
    <div className="flex items-center gap-3 p-3 border border-zinc-200 rounded-lg" data-testid={`mesh-slot-${kind}`}>
      <img src={abs(photo?.thumb_url)} alt="" className="w-16 h-16 rounded-md object-cover border" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{MESH_INFO[kind].title}</p>
        <p className="text-xs text-zinc-500">{photo?.is_placeholder ? "Default photo" : "Uploaded photo"}</p>
      </div>
      <input ref={input} type="file" accept="image/*" className="hidden" data-testid={`mesh-upload-input-${kind}`}
        onChange={(e) => upload(e.target.files?.[0])} />
      <button onClick={() => input.current?.click()} disabled={busy} data-testid={`mesh-upload-btn-${kind}`}
        className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-md bg-[#207024] text-white disabled:opacity-50">
        <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload"}
      </button>
      {!photo?.is_placeholder && (
        <button onClick={reset} className="p-1.5 text-zinc-500 hover:text-zinc-800" title="Use default photo" data-testid={`mesh-reset-btn-${kind}`}>
          <RotateCcw className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export function MeshPhotosSettings() {
  const [photos, setPhotos] = useState(null);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/mesh-photos`).then((r) => setPhotos(r.data)).catch(() => setPhotos({}));
  }, []);
  if (!photos) return null;
  return (
    <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="mesh-photos-settings">
      <div>
        <h2 className="font-semibold">Mesh close-up photos</h2>
        <p className="text-xs text-zinc-500">Shown as tap-to-zoom thumbnails next to the door PetScreen and Solar upgrades and on the customer's online quote. Photos are resized automatically.</p>
      </div>
      {["petscreen", "solar"].map((k) => <Slot key={k} kind={k} photo={photos[k]} onChange={setPhotos} />)}
    </div>
  );
}
